import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isVectorAdmin, resolveProviderUuid } from '@/composables/useProviderAccess'
import ability from '@/plugins/casl/ability'
import { useUserStore } from '@/stores/user'
import api from '@axios'

import type { ProviderSettings, ProviderUser } from './types'

export const useIvProviderStore = defineStore('iv-provider', () => {
  const settings = ref<ProviderSettings | null>(null)
  const users = ref<ProviderUser[]>([])
  const columnMaps = ref<Array<{ id: number; scope: 'personal' | 'provider'; label: string; mapping: Record<string, string> }>>([])

  // True for provider_admins on their own provider, and for vector admins currently
  // routed to a provider (they act on it via the route's provider_uuid — see providerBase()).
  const isAdmin = computed(() => {
    const user = useUserStore()
    const hasProvider = !!user.data?.access?.provider

    return (hasProvider || isVectorAdmin()) && ability.can('manage', 'User')
  })

  function providerBase(): string {
    const uuid = resolveProviderUuid()
    if (!uuid)
      throw new Error('No provider UUID available')

    return `/providers/${uuid}`
  }

  async function fetchSettings() {
    const { data } = await api.get(`${providerBase()}/settings`)

    settings.value = data
  }

  async function updateSettings(payload: Partial<ProviderSettings>) {
    await api.patch(`${providerBase()}/settings`, payload)
    await fetchSettings()
  }

  async function fetchUsers() {
    const { data } = await api.get(`${providerBase()}/users`)

    users.value = data.data ?? data
  }

  async function inviteUser(payload: { name: string; email: string; role: ProviderRole; entitlement_keys: string[] }) {
    await api.post(`${providerBase()}/users/invite`, payload)
    await fetchUsers()
  }

  async function updateUserRole(userUuid: string, role: ProviderRole) {
    await api.patch(`${providerBase()}/users/${userUuid}/role`, { role })
    await fetchUsers()
  }

  async function updateUserStatus(userUuid: string, is_active: boolean) {
    await api.patch(`${providerBase()}/users/${userUuid}/status`, { is_active })
    await fetchUsers()
  }

  async function resendInvitation(userUuid: string) {
    await api.post(`${providerBase()}/users/${userUuid}/resend-invitation`)
  }

  async function fetchColumnMaps() {
    const { data } = await api.get('/insurance/bulk-discovery/mapping')

    columnMaps.value = data.mappings ?? []
  }

  async function createColumnMap(scope: 'personal' | 'provider', mapping: Record<string, string>) {
    await api.post('/insurance/bulk-discovery/mapping', { scope, mapping })
    await fetchColumnMaps()
  }

  async function updateColumnMap(id: number, mapping: Record<string, string>) {
    await api.put(`/insurance/bulk-discovery/mapping/${id}`, { mapping })
    await fetchColumnMaps()
  }

  async function deleteColumnMap(id: number) {
    await api.delete(`/insurance/bulk-discovery/mapping/${id}`)
    columnMaps.value = columnMaps.value.filter(m => m.id !== id)
  }

  function reset() {
    settings.value = null
    users.value = []
    columnMaps.value = []
  }

  return {
    settings,
    users,
    columnMaps,
    isAdmin,
    fetchSettings,
    updateSettings,
    fetchUsers,
    inviteUser,
    updateUserRole,
    updateUserStatus,
    resendInvitation,
    fetchColumnMaps,
    createColumnMap,
    updateColumnMap,
    deleteColumnMap,
    reset,
  }
})
